///<reference path="../clientPoint.service.ts"/>
///<reference path="./clientPoint.section.base.ts"/>
module Meevo.Plugins.ClientPoint {
    'use strict';

    import SecurityKeys = Meevo.Components.Common.SecurityKeys;
    import SecurityParts = Meevo.Components.Common.SecurityParts;
    import IClientPointAdjustmentItem = Meevo.Model.IClientPointAdjustmentItem;
    import IClientPointHistoryItem = Meevo.Model.IClientPointHistoryItem;
    import ISelectItem = Meevo.Model.ISelectItem;
    import PointAdjustmentTypeEnum = Meevo.Model.PointAdjustmentTypeEnum;

    const HISTORY_FILTER: ISelectItem<ClientPointsStatusEnum | null>[] = [
        {value: null, label: 'All'},
        {value: ClientPointsStatusEnum.Active, label: 'Active'},
        {value: ClientPointsStatusEnum.Expired, label: 'Expired'},
        {value: ClientPointsStatusEnum.Added, label: 'Added'},
        {value: ClientPointsStatusEnum.Removed, label: 'Removed'},
        {value: ClientPointsStatusEnum.Earned, label: 'Earned'},
        {value: ClientPointsStatusEnum.Redeemed, label: 'Redeemed'},
    ];

    interface IClientPointHistoryTab extends Meevo.Model.DataMaintenance {
        history: Array<IClientPointHistoryItem>;
    }

    class ClientPointHistoryItem implements IClientPointHistoryItem {
        clientPoint_LId: Guid;
        clientPointDisposition_LId?: Guid;
        adjustmentAmount: number;
        expireDate: Date;
        dateCreated: Date;
        remainingAmount?: number;
        pointAdjustmentTypeEnum?: PointAdjustmentTypeEnum;
        adjustmentNote: string;
        userDisplayName: string;
        clientPointCreatedUserId?: Guid;
        clientPointDeletedUserId?: Guid;
        clientPointDateCreated?: Date;
        clientPointDateDeleted?: Date;
        clientPointCreatedUserDisplayName?: string;
        clientPointDeletedUserDisplayName?: string;
        dispositionCreatedUserId?: Guid;
        dispositionDeletedUserId?: Guid;
        dispositionCreatedUserDisplayName?: string;
        dispositionDeletedUserDisplayName?: string;
        dispositionDateCreated?: Date;
        dispositionDateDeleted?: Date;
        isVoided: boolean;
        locationId?: number;
        storeName?: string;

        get isDeleted(): boolean {
            return !!this.clientPointDateDeleted || !!this.dispositionDateDeleted;
        }

        get hideDeleteButton(): boolean {
            return this.isDeleted && this.adjustmentAmount < 0 ||
                this.isVoided ||
                this.pointAdjustmentTypeEnum === PointAdjustmentTypeEnum.Register;
        }

        constructor(props: IClientPointHistoryItem) {
            _.assign(this, props);
        }
    }

    class ClientPointHistoryTab implements IClientPointHistoryTab {

        private _history: IClientPointHistoryItem[];

        get history() {
            return this._history;
        }

        set history(history: IClientPointHistoryItem[]) {
            this._history = history.map(entry => new ClientPointHistoryItem(entry));
        }

        constructor(props: IClientPointHistoryTab) {
            _.assign(this, props);

            if (props) {
                this.history = props.history;
            }
        }
    }

    /**
     * History Section of Client Points Manager
     */
    export class HistorySection extends Meevo.Plugins.ClientPoint.ClientPointSectionBase<IClientPointHistoryTab> {

        private _isViewingDetailsMode: boolean = false;

        private _model: IClientPointHistoryTab;

        public customButtonsSet: Array<Meevo.Components.Controls.IMaintenanceFormCustomButton>;
        public customFooterInfo: Meevo.Components.Controls.IMaintenanceFormCustomFooterInfo;
        public details: Array<IClientPointAdjustmentItem>;

        public filterValues: ISelectItem<ClientPointsStatusEnum | null>[];
        public filterStatus: ClientPointsStatusEnum | null;
        public homeLocationId: number;

        set model(model: IClientPointHistoryTab) {
            this._model = new ClientPointHistoryTab(model);
        }

        get model() {
            return this._model;
        }

        constructor(sectionId: number,
                    displayNameContentKey: string,
                    template: string,
                    isPrimarySection: boolean,
                    handlesOwnCrud: boolean,
                    baseEndpoint: string,
                    sectionEndpoint: string,
                    loggingService: Meevo.Components.Logging.ILoggingService,
                    dataService: Meevo.Components.BaseClasses.IDataService,
                    identityService: Meevo.Components.Common.IIdentityService,
                    noun: Meevo.Model.NounEnum,
                    protected clientPointService: Meevo.Plugins.ClientPoint.IClientPointService,
                    protected modalService: Meevo.Components.Modal.IModalService,
                    protected busyService: Meevo.Components.Busy.IBusyService,
                    protected $q: ng.IQService,
                    $rootScope: angular.IRootScopeService,
                    protected context: Meevo.Plugins.ClientPoint.ClientPointContext,
                    sectionSecurityKey?: string) {
                        super(sectionId, displayNameContentKey, template, isPrimarySection, handlesOwnCrud, baseEndpoint, sectionEndpoint, loggingService, dataService, identityService, noun,$rootScope,context,sectionSecurityKey);

            this.filterValues = [...HISTORY_FILTER];
            this.filterStatus = null;
            this.homeLocationId = identityService.current().locationId;
        }

        /**
         * Mark an entry as active/inactive
         * @param {IClientPointHistoryItem} entry
         */
        toggleEntry(entry: IClientPointHistoryItem): void {
            let postfix: string = '',
                id: Guid = entry.clientPoint_LId,
                data: Object = {
                    isDelete: !entry.isDeleted,
                    clientId: this.clientPointService.clientPoint.client_TId
                };

            if (entry.adjustmentAmount < 0) {
                postfix = 'disposition/';
                id = entry.clientPointDisposition_LId;
            }

            this.busyService.start();
            this.dataService.customMethodData(`api/${this.baseEndpoint}/${postfix}${id}`, 'PATCH', data)
                .then(() => {
                    this.$q.all([
                        this.clientPointService.getGeneralInformation(),
                        this.preloadHistoryData()
                    ]).finally(() => {
                        entry.isDeleted = !entry.isDeleted;
                        this.busyService.stop();
                    });
                });
        }

        preloadHistoryData(): ng.IPromise<void> {
            this.busyService.start();
            return this.clientPointService.getHistory(this.filterStatus)
                .then(history => {
                    this.model.history = history;
                }).finally(() => {
                    this.busyService.stop();
                });
        }

        hideDeleteButton(entry: IClientPointHistoryItem): boolean {
            return entry.hideDeleteButton ||
                new Date(entry.dateCreated).getDate() !== new Date().getDate() || // US 82437:  the user can delete/undelete to their heart's content on any given day but, as soon as the day changes (00:00:00.000), this is no longer allowed.
                (entry.locationId !== undefined && entry.locationId !== this.homeLocationId);
        }

        /**
         * Handles retrieval of the item from the data service (if it hasn't already been loaded)
         */
        activate(): void {
            this.isViewingDetailsMode = false;

            this.customButtonsSet = [{
                text: 'LABEL_BACK',
                clickHandler: () => {
                    this.isViewingDetailsMode = false;
                },
                classNames: 'meevo-button',
                disableRules: () => false,
                visibilityRules: () => this.isViewingDetailsMode,
                securityRules: SecurityKeys.MANAGEMENT_CLIENTPOINT_TAB_HISTORY + SecurityParts.KEY_PART_READ,
                position: 'right'
            }];

            this.customFooterInfo = {
                className: '',
                template: 'customFooterInfo.html',
                visibilityRules: () => !this.isViewingDetailsMode,
                position: 'right'
            };

            this.busyService.start();
            this.clientPointService.getSettings().finally(() => {
                this.busyService.stop();
            });
        }

        /**
         * Show history for particular entry
         * {IClientPointHistoryItem} entry
         */
        public showDetails(entry: IClientPointHistoryItem): void {
            if (entry.adjustmentAmount < 0) return;

            this.busyService.start();
            this.dataService.customMethodData(`api/${this.baseEndpoint}/${entry.clientPoint_LId}/Adjustment`, 'GET', {})
                .then((response) => {
                    this.details = response.data;
                    this.isViewingDetailsMode = true;
                })
                .finally(() => {
                    this.busyService.stop();
                });
        }

        /**
         * Display model with reason
         * @param {string} adjustmentNote
         */
        public showReason(adjustmentNote: string): void {
            const options: Components.Modal.IModalOptions = {
                templateUrl: 'app/plugins/clientPoint/sectionViews/reason.modal.html',
                clickOutsideToClose: false,
                locals: {
                    adjustmentNote: adjustmentNote
                },
                controller: 'meevo.plugins.clientPoint.ReasonModalController',
                controllerAs: 'vm'
            };

            this.modalService.showCustom(options);
        }

        get isViewingDetailsMode(): boolean {
            return this._isViewingDetailsMode;
        }

        set isViewingDetailsMode(value: boolean) {
            this._isViewingDetailsMode = value;
        }

        get pointAdjustmentTypeEnum(): any {
            return PointAdjustmentTypeEnum;
        }

        get clientPointsStatusEnum(): typeof ClientPointsStatusEnum {
            return ClientPointsStatusEnum;
        }
    }
}
